import { useEffect, useState } from "react";
import { API_BASE, createHeaders } from "../../services/apiClient";
import { themeServiceUtils } from "../../services/themeService";
import {
  THEME_KEY_BY_ID,
  THEME_LABELS,
  getThemeKey,
} from "../utils/themesmap";

const buildEndpointUrl = (userId) => {
  try {
    const url = new URL(`${API_BASE}/chat/messages/thread-counts`);
    if (userId != null) {
      url.searchParams.set("user_id", String(userId));
    }
    return url.toString();
  } catch (error) {
    console.error("Failed to build thread-counts URL", error);
    return `${API_BASE}/chat/messages/thread-counts`;
  }
};

const normalizeText = (value) =>
  typeof value === "string" ? value.trim() : "";

const findThemeKeyByTitle = (title) => {
  if (!title) return null;
  const normalized = normalizeText(title).toLowerCase();
  if (!normalized) return null;
  for (const [key, label] of Object.entries(THEME_LABELS)) {
    if (normalizeText(label).toLowerCase() === normalized) {
      return key;
    }
  }
  return null;
};

const normalizeThemeKey = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) {
    return THEME_KEY_BY_ID[trimmed] || null;
  }
  const normalized = trimmed
    .toLowerCase()
    .replace(/^themes\./, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/(^_|_$)/g, "");
  if (!normalized) return null;
  return normalized;
};

const resolveThemeKey = (row, threadId, title) => {
  const directCandidates = [
    row?.theme_key,
    row?.themeKey,
    row?.theme?.key,
    row?.theme?.slug,
    row?.theme?.theme_key,
    row?.theme?.themeKey,
    row?.theme?.id,
    row?.theme?.theme_id,
    row?.slug,
    row?.thread_slug,
    row?.threadSlug,
    row?.theme_slug,
    row?.themeSlug,
    row?.topic_slug,
    row?.topicSlug,
  ];
  for (const candidate of directCandidates) {
    const key = normalizeThemeKey(candidate);
    if (key) return key;
  }

  const idCandidates = [
    threadId,
    row?.topic_id,
    row?.topicId,
    row?.theme_id,
    row?.themeId,
    row?.id,
  ];
  for (const candidate of idCandidates) {
    const key = normalizeThemeKey(candidate);
    if (key) return key;
  }

  const themeObjectKey = row?.theme ? getThemeKey(row.theme) : null;
  if (themeObjectKey) return themeObjectKey;

  const labelCandidates = [
    title,
    row?.thread_title,
    row?.threadTitle,
    row?.name,
    row?.theme?.name,
    row?.theme?.label,
  ];
  for (const candidate of labelCandidates) {
    const key = findThemeKeyByTitle(candidate);
    if (key) return key;
  }

  return null;
};

const pickSlug = (row, threadId, title, themeKey) => {
  if (themeKey) {
    return themeKey.replace(/_/g, "-");
  }
  const candidateFields = [
    row?.slug,
    row?.thread_slug,
    row?.threadSlug,
    row?.theme_slug,
    row?.themeSlug,
    row?.topic_slug,
    row?.topicSlug,
  ];
  let numericFallback = null;
  for (const value of candidateFields) {
    const sanitized = themeServiceUtils.sanitizeThemeId(value);
    if (sanitized && !/^\d+$/.test(sanitized)) {
      return sanitized;
    }
    if (sanitized && numericFallback == null) {
      numericFallback = sanitized;
    }
  }

  const idCandidates = [
    threadId,
    row?.topic_id,
    row?.topicId,
    row?.theme_id,
    row?.themeId,
  ];
  for (const candidate of idCandidates) {
    const sanitized = themeServiceUtils.sanitizeThemeId(candidate);
    if (sanitized && !/^\d+$/.test(sanitized)) {
      return sanitized;
    }
    if (sanitized && numericFallback == null) {
      numericFallback = sanitized;
    }
  }

  const fallback = themeServiceUtils.sanitizeThemeId(title);
  if (fallback && !/^\d+$/.test(fallback)) {
    return fallback;
  }

  return numericFallback;
};

const aggregateRowsByTheme = (rows) => {
  const map = new Map();
  rows.forEach((row, idx) => {
    const key =
      row.theme_key ||
      (row.slug && !/^\d+$/.test(row.slug) ? row.slug : null) ||
      (row.thread_id != null ? `thread-${row.thread_id}` : `row-${idx}`);
    const existing = map.get(key);
    const messageCount = Number(row.message_count) || 0;
    if (existing) {
      existing.message_count =
        (existing.message_count || 0) + messageCount;
      if (!existing.thread_id && row.thread_id != null) {
        existing.thread_id = row.thread_id;
      }
      if (!existing.slug && row.slug) {
        existing.slug = row.slug;
      }
      if (!existing.theme_key && row.theme_key) {
        existing.theme_key = row.theme_key;
      }
      if (!existing.title && row.title) {
        existing.title = row.title;
      }
    } else {
      map.set(key, {
        ...row,
        message_count: messageCount,
      });
    }
  });
  return Array.from(map.values());
};

export const useUserThreadMessageCounts = (userId) => {
  const [threads, setThreads] = useState([]);
  const [metadata, setMetadata] = useState({
    thread_count: 0,
    total_messages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) {
      setThreads([]);
      setMetadata({ thread_count: 0, total_messages: 0 });
      setLoading(false);
      setError("");
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchCounts = async () => {
      setLoading(true);
      setError("");
      try {
        const headers = createHeaders({ json: true, userId });
        const response = await fetch(buildEndpointUrl(userId), {
          method: "GET",
          headers,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`thread-counts failed (${response.status})`);
        }
        const payload = await response.json();
        if (cancelled) return;
        const rows = Array.isArray(payload?.threads) ? payload.threads : [];
        const normalizedRows = rows.map((row) => {
          const threadId = row.thread_id ?? row.threadId ?? null;
          const title =
            row.title || row.thread_title || row.threadTitle || "Untitled Thread";
          const themeKey = resolveThemeKey(row, threadId, title);
          return {
            thread_id: threadId,
            title,
            theme_key: themeKey || null,
            message_count: row.message_count ?? row.messageCount ?? 0,
            slug: pickSlug(row, threadId, title, themeKey),
          };
        });
        const aggregatedRows = aggregateRowsByTheme(normalizedRows);
        setThreads(aggregatedRows);
        setMetadata({
          thread_count: payload?.thread_count ?? aggregatedRows.length,
          total_messages:
            payload?.total_messages ??
            aggregatedRows.reduce(
              (sum, row) => sum + (row.message_count ?? 0),
              0
            ),
        });
      } catch (err) {
        if (cancelled) return;
        setThreads([]);
        setMetadata({ thread_count: 0, total_messages: 0 });
        setError(err?.message || "Failed to load discussion threads");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCounts();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId]);

  return {
    threads,
    threadCount: metadata.thread_count,
    totalMessages: metadata.total_messages,
    loading,
    error,
  };
};

export default useUserThreadMessageCounts;
